const EVENT_NAME = 'appEvent'
const EPRINT_EVENT_PREFIX = 'eprint_'
const LOGIN_POPUP_EVENT_PREFIX = 'login_popup_'

// 印刷タイプのラベルマッピング
const PRINT_TYPE_LABELS = {
  1: '白黒片面A4_1枚',
  2: '白黒両面A4_1枚',
  4: '白黒片面A3_1枚',
  6: 'カラー片面A4_1枚',
  7: 'カラー片面A3_1枚',
  8: 'カラー片面B4_1枚',
  10: '白黒片面A4_2枚',
  11: '白黒両面A4_2枚',
  12: 'カラー片面A4_2枚',
  15: '写真プリント'
}

// 印刷タイプの価格マッピング
const PRINT_TYPE_PRICES = {
  1: 450,
  2: 450,
  4: 450,
  6: 450,
  7: 450,
  8: 450,
  10: 450,
  11: 450,
  12: 450,
  15: 450
}

// コンビニ店舗のラベルマッピング
const STORE_LABELS = {
  seven: 'セブンイレブン',
  others: 'ファミリーマート・ローソン等',
  lawson: 'ローソン'
}

// 決済方法のラベルマッピング
const PAYMENT_METHOD_LABELS = {
  stripe: 'クレジットカード',
  paypay: 'PayPay',
  postpaid: '店舗後払い'
}

// firebase の providerId から GA4 の method パラメータへのマッピング。
// GA4 の login / sign_up の method は英数字で揃えたいので日本語ラベルにはしない。
// LINE は Firebase の provider ではなく customToken 経由なので providerData に現れない。
// メールリンクも providerId は 'password' になり通常のパスワードログインと区別できない。
// この2つは認証を開始した側が AUTH_METHOD_HINT_KEY にヒントを残す。
const AUTH_METHOD_LABELS = {
  password: 'password',
  'google.com': 'google',
  'apple.com': 'apple',
  'facebook.com': 'facebook'
}

// アカウント作成時刻と最終ログイン時刻が近ければ新規登録とみなす窓。
// 復帰ユーザーの creationTime は日単位で古いため、この程度緩くても誤判定しない。
const FRESH_USER_WINDOW_MS = 5 * 60 * 1000

// lastSignInTime が現在からこの範囲内なら「今この読み込みで認証が成立した」とみなす窓。
// Firebase は token のリフレッシュでは lastSignInTime を更新せず、実際のサインイン時のみ
// 更新するため、セッション復元と新規認証をこれで判別できる。
const RECENT_SIGN_IN_WINDOW_MS = 5 * 60 * 1000

// 認証試行のヒント受け渡し用。認証を開始した側が置き、onAuthStateChanged 側が読んで消す。
// 2つの役割を兼ねる:
//   (1) 実際に使った認証手段の記録。user.providerData は「アカウントに紐づく全プロバイダ」で
//       今回使った手段を表さない（password と google を併用しているユーザーが google で
//       ログインしても providerData[0] が password になりうる）
//   (2) 「今この読み込みで認証が成立した」ことの印。これがあれば端末時計に依存せず判定できる
const AUTH_HINT_KEY = '_yagish_auth_hint'
// ヒントの有効期限。認証開始から成立までにこれ以上かかることは無いとみなす。
// 端末内で置いて端末内で読むだけなので、端末時計の絶対値ずれには影響されない（差分しか見ない）。
const AUTH_HINT_TTL_MS = 10 * 60 * 1000
// 同一サインインを二重計上しないための記録キー（uid + lastSignInTime）。
// サインイン直後のリロードでも onAuthStateChanged は再度発火するため、これが無いと重複する。
const AUTH_REPORTED_KEY = '_yagish_auth_reported'
// sign_up を送った uid の記録。新規登録直後にログアウトして再ログインすると
// creationTime との差がまだ窓内なので、これが無いと2回目も sign_up になり獲得数を水増しする。
const SIGNUP_REPORTED_UID_KEY = '_yagish_signup_reported_uid'

const safeLocalStorage = {
  get: function (key) {
    try {
      return window.localStorage.getItem(key)
    } catch (e) {
      // プライベートブラウジング等で localStorage が使えない場合。
      // 計測のために本体機能を落とすことはしない。
      return null
    }
  },
  set: function (key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (e) {}
  },
  remove: function (key) {
    try {
      window.localStorage.removeItem(key)
    } catch (e) {}
  }
}

const parseTime = function (value) {
  const t = Date.parse(value || '')
  return isNaN(t) ? null : t
}

// 認証試行のヒントを読む。期限切れ・壊れている場合は破棄して null を返す。
// 認証が失敗した経路でヒントが残っていると、次に別の手段でログインしたときに
// 古い method で誤計上されるため、期限切れは必ず捨てる。
const readAuthHint = function () {
  const raw = safeLocalStorage.get(AUTH_HINT_KEY)
  if (!raw) {
    return null
  }
  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    parsed = null
  }
  if (!parsed || !parsed.method || typeof parsed.at !== 'number') {
    safeLocalStorage.remove(AUTH_HINT_KEY)
    return null
  }
  if (Date.now() - parsed.at > AUTH_HINT_TTL_MS) {
    safeLocalStorage.remove(AUTH_HINT_KEY)
    return null
  }
  return parsed
}

// 新規登録かどうかの判定。作成時刻と最終ログイン時刻が近ければ新規とみなす。
// 判定できないときは false（=ログイン扱い）に倒す。新規を login に混ぜる方が、
// 復帰を sign_up に混ぜて獲得数を過大報告するより害が小さい。
const isFreshlyCreatedUser = function (user) {
  const metadata = user && user.metadata
  const created = parseTime(metadata && metadata.creationTime)
  const lastSignIn = parseTime(metadata && metadata.lastSignInTime)
  if (created === null || lastSignIn === null) {
    return false
  }
  return Math.abs(lastSignIn - created) <= FRESH_USER_WINDOW_MS
}

const gtmDataLayer = {
  install: function (Vue, options) {
    const dataLayer = window.dataLayer || []

    Vue.prototype.$gtm = {
      // 既存メソッド: エラー通知
      sendError: function (action, label) {
        dataLayer.push({
          event: EVENT_NAME,
          eventCategory: 'appError',
          eventAction: action || 'defaultAction',
          eventLabel: label || 'defaultLabel'
        })
      },

      // コンビニプリント専用: イベント送信
      sendEprintEvent: function (eventName, params = {}) {
        const baseParams = {
          event: EPRINT_EVENT_PREFIX + eventName,
          timestamp: new Date().toISOString(),
          device_type: (Vue.prototype.$deviceLib && Vue.prototype.$deviceLib.isMobileDevice) ? 'SP' : 'PC',
          app_flag: (Vue.prototype.$site && Vue.prototype.$site.appFlag) || '',
          is_authenticated: (Vue.prototype.$firebase && Vue.prototype.$firebase.isAuthenticated) || false
        }

        dataLayer.push({
          ...baseParams,
          ...params
        })

        // デバッグログ（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM Eprint Event]', eventName, { ...baseParams, ...params })
        }
      },

      // 認証試行のヒントを残す。認証を「開始する」直前に呼ぶ。
      // リダイレクトやページ遷移を跨いで onAuthStateChanged 側に伝える必要があるため
      // localStorage を使う。呼び忘れても計上自体は落ちない（method が粗くなるだけ）ので、
      // 経路追加時に付け忘れても無言で欠測にはならない。
      hintAuthMethod: function (method) {
        safeLocalStorage.set(AUTH_HINT_KEY, JSON.stringify({ method, at: Date.now() }))
      },

      // 認証が失敗したときにヒントを捨てる。残したままにすると、次に別の手段でログインした際に
      // 古いヒントが優先されて誤った method で計上される。
      clearAuthMethodHint: function () {
        safeLocalStorage.remove(AUTH_HINT_KEY)
      },

      // 認証成立の計測: GA4 推奨イベント login / sign_up を送る。
      //
      // ⚠️ 呼び出しは firebase-lib.js の onAuthStateChanged 1箇所だけにすること。
      // 以前は認証の呼び出し箇所（signInEmailPasswd / signOnEmailPasswd / getRedirectResult /
      // signInWithCustomToken / signInWithEmailLink）に個別に挿していたが、その構造では
      // 未知の経路を取りこぼす。実際に4箇所漏れて実登録の1割弱しか計上できていなかった
      // （2026-07-29、DB の member 実数と突き合わせて判明）。
      // onAuthStateChanged は経路に関係なく認証成立で必ず1回通る唯一の関門なので、
      // ここに置けば経路が増えても自動的に計上される。
      //
      // セッション復元との判別は、まずヒントの有無で行う（認証を開始した側が置いたヒントが
      // あれば、今この読み込みで認証が成立したと確定できる。端末時計を見ないので安全）。
      // ヒントが無い未知の経路だけ user.metadata.lastSignInTime の窓で判定する。Firebase は
      // token のリフレッシュでは lastSignInTime を更新せず実際のサインイン時のみ更新するため、
      // 「現在から RECENT_SIGN_IN_WINDOW_MS 以内」なら直近の認証と推定できる。
      // サインイン直後のリロードも窓内に入るので、uid + lastSignInTime を記録して二重計上を防ぐ。
      //
      // ── 既知の限界（2026-07-29 時点で意図的に受容している） ──
      // (1) 新規登録判定は近似。isNewUser は UserCredential にしか無く onAuthStateChanged では
      //     取得できないため、creationTime と lastSignInTime の差で代用している。よって
      //     「登録から FRESH_USER_WINDOW_MS 以内に別端末でログイン」すると、その端末には
      //     SIGNUP_REPORTED_UID_KEY が無いので sign_up が二重に計上される。
      // (2) localStorage が拒否される環境（Safari プライベート等）では二重計上の抑止が効かず、
      //     サインイン後 RECENT_SIGN_IN_WINDOW_MS 以内のリロードごとに再送される。
      //     状態を保存できない環境で状態に依存した抑止をしている構造上の限界。
      // どちらも過大計上の方向で、影響範囲は「元々9割取りこぼしていた」ことに比べれば桁違いに
      // 小さいため、精度を上げるための分岐追加より単純さを優先した。
      sendAuthEvent: function (user) {
        if (!user) {
          return
        }
        const lastSignIn = parseTime(user.metadata && user.metadata.lastSignInTime)
        if (lastSignIn === null) {
          // 判定材料が無い場合は送らない。復元を login として撃つより無計上の方が害が小さい。
          return
        }

        const hint = readAuthHint()

        // 「今この読み込みで認証が成立したか」の判定。
        // ヒントがあれば確定（端末時計に依存しない）。無い場合だけ lastSignInTime の窓で
        // フォールバック判定する。窓判定は端末時計とサーバー時刻の比較なので端末の時刻ずれに
        // 弱いが、これはヒントを置いていない未知の経路を拾うための保険であり、
        // ここを落としても既知の経路は影響を受けない。
        if (!hint && Date.now() - lastSignIn > RECENT_SIGN_IN_WINDOW_MS) {
          // セッション復元とみなす。認証行為ではないので計上しない。
          return
        }

        const reportKey = user.uid + ':' + lastSignIn
        if (safeLocalStorage.get(AUTH_REPORTED_KEY) === reportKey) {
          // 同じサインインを既に計上済み（リロード等）。
          return
        }

        // 実際に使った手段はヒントが正。user.providerData はアカウントに紐づく全プロバイダの
        // 一覧で、先頭要素が今回使った手段とは限らない（password と google を併用している
        // ユーザーが google でログインしても providerData[0] が password になりうる）。
        // ヒントが無い場合の providerData 参照はあくまで粗い推定。
        const providerId = user.providerData && user.providerData.length
          ? user.providerData[0].providerId
          : null
        const method = (hint && hint.method) || AUTH_METHOD_LABELS[providerId] || 'unknown'

        // 新規登録は uid につき一度だけ。creationTime との差だけで判定すると、登録直後に
        // ログアウトして再ログインした場合に2回目も sign_up になり獲得数を水増しする。
        const alreadyCountedAsSignup = safeLocalStorage.get(SIGNUP_REPORTED_UID_KEY) === user.uid
        const isSignUp = isFreshlyCreatedUser(user) && !alreadyCountedAsSignup

        const eventName = isSignUp ? 'sign_up' : 'login'
        const params = {
          method,
          device_type: (Vue.prototype.$deviceLib && Vue.prototype.$deviceLib.isMobileDevice) ? 'SP' : 'PC'
        }

        safeLocalStorage.set(AUTH_REPORTED_KEY, reportKey)
        safeLocalStorage.remove(AUTH_HINT_KEY)
        if (isSignUp) {
          safeLocalStorage.set(SIGNUP_REPORTED_UID_KEY, user.uid)
        }

        // GA4 へ直接送る（GTM 側のタグ設定を待たずに数値が取れる。sendEprintPurchase と同じ方式）
        if (window.gtag) {
          window.gtag('event', eventName, params)
        }

        // dataLayer にも流す（GTM 側で別の配信先に繋ぐ場合用）
        dataLayer.push({
          event: eventName,
          ...params
        })

        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM Auth Event]', eventName, params)
        }
      },

      // 完了画面のログインポップアップ専用: イベント送信
      sendLoginPopupEvent: function (eventName, params = {}) {
        const baseParams = {
          event: LOGIN_POPUP_EVENT_PREFIX + eventName,
          timestamp: new Date().toISOString(),
          device_type: (Vue.prototype.$deviceLib && Vue.prototype.$deviceLib.isMobileDevice) ? 'SP' : 'PC',
          is_authenticated: (Vue.prototype.$firebase && Vue.prototype.$firebase.isAuthenticated) || false
        }

        dataLayer.push({
          ...baseParams,
          ...params
        })

        // デバッグログ（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM LoginPopup Event]', eventName, { ...baseParams, ...params })
        }
      },

      // コンビニプリント専用: GA4 Eコマースイベント（決済成功時）
      sendEprintPurchase: function (transactionData) {
        if (window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: transactionData.transaction_id,
            value: transactionData.value,
            currency: 'JPY',
            items: [{
              item_id: transactionData.print_type,
              item_name: transactionData.product_name || PRINT_TYPE_LABELS[transactionData.print_type] || 'コンビニプリント',
              item_category: 'コンビニプリント',
              price: transactionData.value,
              quantity: 1
            }]
          })
        }

        // dataLayerにも送信（GTM連携用）
        dataLayer.push({
          event: 'eprint_purchase',
          ecommerce: {
            transaction_id: transactionData.transaction_id,
            value: transactionData.value,
            currency: 'JPY',
            items: [{
              item_id: transactionData.print_type,
              item_name: transactionData.product_name || PRINT_TYPE_LABELS[transactionData.print_type] || 'コンビニプリント',
              item_category: 'コンビニプリント',
              price: transactionData.value,
              quantity: 1
            }]
          }
        })

        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM Eprint Purchase]', transactionData)
        }
      },

      // ファネルセッションID生成
      generateFunnelSessionId: function () {
        return 'eprint_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      },

      // 印刷タイプのラベル取得
      getPrintTypeLabel: function (printType) {
        return PRINT_TYPE_LABELS[printType] || `印刷タイプ${printType}`
      },

      // 印刷タイプの価格取得
      getPrintTypePrice: function (printType) {
        return PRINT_TYPE_PRICES[printType] || 450
      },

      // コンビニ店舗のラベル取得
      getStoreLabel: function (storeKey) {
        return STORE_LABELS[storeKey] || storeKey
      },

      // 決済方法のラベル取得
      getPaymentMethodLabel: function (methodKey) {
        return PAYMENT_METHOD_LABELS[methodKey] || methodKey
      }
    }
  }
}
export default gtmDataLayer
